"use client";

import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { Link } from "@components/cms/link/Link";
import type { Document } from "@contentful/rich-text-types";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { GridItem } from "@ecco/ui/grid";
import { NextImage } from "@ecco/ui/next-image";
import type { ResolvedAsset as Asset } from "@contentful-api/cmsResolvedTypes";
import { EditorialContentCardStyles, EditorialWidgetStyles } from "./ProductListStyles";
import type { IPagePath, ILink } from "@contentful-api/types/contentful";
import type { IResolvedMediaAsset } from "@lib/contentful/api/helpers/resolveMediaAsset";

interface IGridPlacement {
    colSpan?: number | number[];
    rowStart?: number | number[];
    colStart?: number | number[];
    rowSpan?: number;
    fontSize?: string | string[];
}

interface IProps {
    image?: Asset;
    asset?: IResolvedMediaAsset;
    title?: string;
    styles: IGridPlacement | null;
    link?: IPagePath | ILink;
    widgetSize?: string;
    isVisible?: boolean;
    description?: unknown;
    isContentCard?: boolean;
}

function getGridPlacement(styles: IGridPlacement) {
    const { fontSize: _fontSize, ...placement } = styles;
    return placement;
}

function isRichTextDocument(value: unknown): value is Document {
    if (!value || typeof value !== "object") {
        return false;
    }

    const candidate = value as { nodeType?: unknown; content?: unknown };
    return candidate.nodeType === "document" && Array.isArray(candidate.content);
}

function WidgetDescription({ description }: { description?: unknown }) {
    if (!description) {
        return null;
    }

    if (isRichTextDocument(description)) {
        return (
            <Box sx={EditorialContentCardStyles.description}>
                <RichText data={description} />
            </Box>
        );
    }

    if (typeof description === "string") {
        return <Text sx={EditorialContentCardStyles.description}>{description}</Text>;
    }

    return null;
}

export const EditorialWidget = ({
    image,
    asset,
    title,
    styles,
    widgetSize,
    link,
    isVisible,
    description,
    isContentCard = false,
}: IProps) => {
    const mapWidgetSizeToImageSize = (size?: string) => {
        const sizeMap: Record<string, string> = {
            Small: "100vw",
            Tall: "80vw",
            Big: "100vw",
        };
        return (size && sizeMap[size]) || "100vw";
    };

    if (!styles) {
        return null;
    }

    const gridPlacement = getGridPlacement(styles);
    const linkLabel =
        link?.sys?.contentType?.sys?.id !== "pagePath" ? link?.fields?.label : undefined;
    const imageAccessibleName = image?.fields?.title || image?.fields?.description;
    const overlayAccessibleName = title || linkLabel || imageAccessibleName;
    const overlayBox = (
        <Box
            sx={{
                ...EditorialWidgetStyles,
                ...(title ? { backgroundColor: "rgba(0, 0, 0, 0.28)" } : {}),
            }}
        >
            <Text color="white" fontSize={styles.fontSize} fontWeight="bold">
                {title}
            </Text>
        </Box>
    );

    return (
        <GridItem
            position="relative"
            height={isContentCard ? "auto" : ["575px", "100%", "100%"]}
            alignSelf={isContentCard ? "start" : undefined}
            display={isVisible ? "flex" : "none"}
            {...gridPlacement}
        >
            {isContentCard ? (
                <Flex {...EditorialContentCardStyles.wrapper}>
                    <Box {...EditorialContentCardStyles.imageWrapper}>
                        <NextImage
                            src={asset?.url}
                            objectFit="cover"
                            sizes={["200vw", "200vw", mapWidgetSizeToImageSize(widgetSize)]}
                            alt={asset?.title}
                            fill
                        />
                    </Box>
                    <Flex {...EditorialContentCardStyles.content}>
                        {title ? <Text sx={EditorialContentCardStyles.title}>{title}</Text> : null}
                        <WidgetDescription description={description} />
                        {link && linkLabel ? (
                            <Box {...EditorialContentCardStyles.linkWrapper}>
                                <Link link={link}>
                                    <Box as="span" {...EditorialContentCardStyles.linkLabel}>
                                        {linkLabel}
                                    </Box>
                                </Link>
                            </Box>
                        ) : null}
                    </Flex>
                </Flex>
            ) : (
                <>
                    <NextImage
                        src={asset?.url}
                        objectFit="cover"
                        display="flex"
                        sizes={["200vw", "200vw", mapWidgetSizeToImageSize(widgetSize)]}
                        alt={asset?.title}
                        fill
                    />
                    {link && overlayAccessibleName ? (
                        <Link link={link} aria-label={title ? undefined : overlayAccessibleName}>
                            {overlayBox}
                        </Link>
                    ) : (
                        overlayBox
                    )}
                </>
            )}
        </GridItem>
    );
};
